import React, { useEffect, useState } from "react";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";

import { MachineQrCodePreview } from "./MachineQrCodePreview";
import {
  fetchMachineConfiguration,
  fetchMachineGroups,
  fetchMachineQrCode,
  readApiError,
} from "./api/machineEditorApi";
import {
  MachineConfiguration,
  MachineQrCode,
} from "./machineEditor.typing";
import { LogMachineData } from "../../Widgets/Log/log.typing";

interface MachineQrCodeDialogProps {
  open: boolean;
  machineId?: string | null;
  fallbackMachine?: Partial<LogMachineData> | null;
  onClose: () => void;
}

const SURFACE = "#383838";
const surface = { backgroundColor: SURFACE, color: "#fff" };

export const MachineQrCodeDialog: React.FC<MachineQrCodeDialogProps> = ({
  open,
  machineId,
  fallbackMachine,
  onClose,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [machine, setMachine] = useState<MachineConfiguration | null>(null);
  const [qrCode, setQrCode] = useState<MachineQrCode | null>(null);

  useEffect(() => {
    if (!open || !machineId) {
      setMachine(null);
      setQrCode(null);
      setError(null);
      setIsLoading(false);
      return;
    }

    let isMounted = true;
    setIsLoading(true);
    setError(null);

    const loadData = async () => {
      try {
        const [configurationResult, qrCodeResult, groupsResult] =
          await Promise.allSettled([
            fetchMachineConfiguration(machineId),
            fetchMachineQrCode(machineId),
            fetchMachineGroups(),
          ]);

        if (!isMounted) return;

        if (qrCodeResult.status === "rejected") {
          throw qrCodeResult.reason;
        }

        const qrCodeData = qrCodeResult.value;
        let machineConfig: MachineConfiguration;

        if (configurationResult.status === "fulfilled") {
          const config = configurationResult.value;
          let groupName = config.GroupName;
          if (!groupName && groupsResult.status === "fulfilled") {
            groupName =
              groupsResult.value.find((g) => g.Id === config.GroupId)
                ?.GroupName ?? null;
          }
          machineConfig = {
            ...config,
            GroupName: groupName,
          };
        } else {
          machineConfig = {
            MachineId: machineId,
            MachineName: fallbackMachine?.MachineName ?? "",
            MachineCode: fallbackMachine?.MachineCode ?? "",
            GroupId: null,
            GroupName: null,
            IsAvailable: true,
          };
        }

        setMachine(machineConfig);
        setQrCode(qrCodeData);
      } catch (err) {
        if (!isMounted) return;
        setError(
          readApiError(err, "Failed to load the QR code. Please try again.")
        );
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadData();

    return () => {
      isMounted = false;
    };
  }, [open, machineId, fallbackMachine?.MachineName, fallbackMachine?.MachineCode]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xs"
      PaperProps={{ style: surface }}
    >
      <DialogTitle style={surface}>Machine QR code</DialogTitle>

      <DialogContent style={surface}>
        {error && (
          <div style={{ color: "#ff8f8f", paddingBottom: "12px" }}>
            {error}
          </div>
        )}

        {isLoading ? (
          <div style={{ padding: "32px", textAlign: "center" }}>
            <CircularProgress size={28} />
          </div>
        ) : (
          machine &&
          qrCode && (
            <MachineQrCodePreview
              machine={machine}
              qrCode={qrCode}
              stage="confirmed"
              signalReceived={true}
              attempts={0}
              onRetry={() => {}}
            />
          )
        )}
      </DialogContent>

      <DialogActions style={surface}>
        <Button
          variant="contained"
          color="success"
          sx={{ color: "#fff" }}
          onClick={onClose}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MachineQrCodeDialog;
